class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index
    /** Product card FAB that opened the modal (spinner until section loads). */
    this._fabTrigger = null;

    this.initEvents();
  }

  initEvents() {
    this.closeButton.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Handle quick-add buttons - both direct add and modal
    document.addEventListener("click", (e) => {
      // Card-level qty stepper (only present in direct-add state)
      const qtyBtn = e.target.closest(
        "[data-card-qty-minus], [data-card-qty-plus]"
      );
      if (qtyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const stepper = qtyBtn.closest("[data-card-qty-stepper]");
        if (!stepper) return;
        const valEl = stepper.querySelector("[data-card-qty-value]");
        if (!valEl) return;
        const cur = parseInt(String(valEl.textContent || "1").trim(), 10) || 1;
        const dir = qtyBtn.matches("[data-card-qty-plus]") ? 1 : -1;
        const next = Math.max(1, Math.min(99, cur + dir));
        valEl.textContent = String(next);
        const minus = stepper.querySelector("[data-card-qty-minus]");
        if (minus) minus.disabled = next <= 1;
        return;
      }

      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (!quickAddBtn) return;

      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      const hasVariants = quickAddBtn.dataset.hasVariants === "true";
      const variantId = quickAddBtn.dataset.variantId;

      if (!productHandle) return;

      // Direct add to cart for single variant products
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, quickAddBtn);
      } else {
        this.open(productHandle, quickAddBtn);
      }
    });
  }

  clearFabTriggerLoading() {
    if (this._fabTrigger) {
      this._fabTrigger.classList.remove("is-quick-add-loading");
      this._fabTrigger.disabled = false;
      this._fabTrigger = null;
    }
  }

  open(productHandle, triggerButton = null) {
    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.clearFabTriggerLoading();
    this.currentProductHandle = productHandle;
    this._fabTrigger = triggerButton || null;
    if (this._fabTrigger) {
      this._fabTrigger.classList.add("is-quick-add-loading");
      this._fabTrigger.disabled = true;
    }

    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    this.clearFabTriggerLoading();
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
  }

  async addToCartDirect(variantId, button) {
    const labelElement = button.querySelector(".quick-add-btn-label");
    const originalText =
      labelElement?.textContent?.trim() || button.textContent?.trim() || "";
    const originalAria = button.getAttribute("aria-label") || "";

    const qtyHost = button.closest(".card__quick-add--with-qty");
    const qtyValEl = qtyHost && qtyHost.querySelector("[data-card-qty-value]");
    const qtyParsed = qtyValEl
      ? parseInt(String(qtyValEl.textContent || "1").trim(), 10)
      : 1;
    const quantity = !qtyParsed || qtyParsed < 1 ? 1 : qtyParsed;

    button.disabled = true;
    button.classList.add("is-quick-add-loading");
    if (labelElement) {
      labelElement.textContent = "Adding...";
    } else {
      button.setAttribute("aria-label", "Adding...");
    }

    try {
      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          productId: button.dataset.productId,
        });

        button.classList.remove("is-quick-add-loading");

        if (labelElement) {
          labelElement.textContent = "Added!";
        } else {
          button.setAttribute("aria-label", "Added!");
        }

        setTimeout(() => {
          button.disabled = false;
          if (labelElement) {
            labelElement.textContent = originalText;
          } else {
            button.setAttribute("aria-label", originalAria);
          }
        }, 1500);
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);

      publish(PUB_SUB_EVENTS.cartError, {
        error: error.message,
        context: "quick-add-direct",
        productId: button.dataset.productId,
      });

      if (labelElement) {
        labelElement.textContent = "Error - Try Again";
      } else {
        button.setAttribute("aria-label", "Error - Try Again");
      }

      button.classList.remove("is-quick-add-loading");
      setTimeout(() => {
        button.disabled = false;
        if (labelElement) {
          labelElement.textContent = originalText;
        } else {
          button.setAttribute("aria-label", originalAria);
        }
      }, 2000);
    }
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p class="loading-text">Loading product</p>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button onclick="quickAddModal.close()">Close</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=product-quick-add`
      );
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productHTML = doc.querySelector(".quick-add-product");

      if (!productHTML) throw new Error("Invalid product content");

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(productHTML);

      this.currentProductData = null;

      this.initProductScripts();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    } finally {
      this.clearFabTriggerLoading();
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    if (typeof window.initProductMainQuickAdd === "function") {
      window.initProductMainQuickAdd(productContainer);
      this.initQuickAddInjectedUi(productContainer);
      return;
    }

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;
    const variantsData = productContainer.dataset.productVariants;
    if (variantsData) {
      try {
        this.currentProductData = JSON.parse(variantsData);
      } catch (e) {
        this.currentProductData = null;
      }
    }
    this.initImageSlider(productContainer);
    this.initImageZoom(productContainer);
    this.initVariantSelection(productContainer, productHandle);
    this.initQuantitySelector(productContainer);
    this.initFormSubmission(productContainer);
  }

  /** Description clamp + size guide modal (section inline scripts do not run when HTML is injected). */
  initQuickAddInjectedUi(container) {
    if (!container) return;
    const area = container.querySelector("[data-product-section]");
    const desc = area && area.querySelector("[data-product-description-clamp]");
    const readBtn = area && area.querySelector("[data-product-description-read-more]");
    if (desc && readBtn) {
      const readLbl = readBtn.querySelector("[data-product-description-read-label]");
      const updateReadMoreVisibility = () => {
        if (readBtn.classList.contains("is-expanded")) return;
        desc.classList.add("is-clamped");
        const isOverflowing = desc.scrollHeight > desc.clientHeight + 1;
        readBtn.style.display = isOverflowing ? "inline-flex" : "none";
        if (!isOverflowing) desc.classList.remove("is-clamped");
      };
      updateReadMoreVisibility();
      readBtn.addEventListener("click", () => {
        const expanded = readBtn.classList.toggle("is-expanded");
        desc.classList.toggle("is-clamped", !expanded);
        readBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
        if (readLbl) readLbl.textContent = expanded ? "Read less" : "Read more";
      });
      window.addEventListener("resize", updateReadMoreVisibility);
    }
    function lockScrollForSizeChart() {
      document.documentElement.classList.add("scroll-locked");
      document.body.style.overflow = "hidden";
      if (window.lenis && typeof window.lenis.stop === "function") window.lenis.stop();
    }
    function unlockScrollForSizeChart() {
      document.documentElement.classList.remove("scroll-locked");
      document.body.style.overflow = "";
      if (window.lenis && typeof window.lenis.start === "function") window.lenis.start();
    }
    function openSizeChartModal(m) {
      if (!m) return;
      m.classList.add("is-open");
      m.setAttribute("aria-hidden", "false");
      lockScrollForSizeChart();
      const panel = m.querySelector(".product-main__size-chart-panel");
      if (panel && typeof panel.focus === "function") panel.focus();
    }
    function closeSizeChartModal(m) {
      if (!m) return;
      m.classList.remove("is-open");
      m.setAttribute("aria-hidden", "true");
      unlockScrollForSizeChart();
    }
    container.querySelectorAll("[data-size-chart-open]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const tid = btn.getAttribute("data-size-chart-target");
        const m = tid
          ? document.querySelector(tid)
          : container.querySelector("[data-size-modal]");
        openSizeChartModal(m);
      });
    });
    container.querySelectorAll("[data-size-modal]").forEach((m) => {
      m.querySelectorAll("[data-size-modal-close]").forEach((btn) => {
        btn.addEventListener("click", () => closeSizeChartModal(m));
      });
      m.addEventListener("click", (e) => {
        if (e.target === m) closeSizeChartModal(m);
      });
    });
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const counter = container.querySelector(
      ".quick-add-counter .current-slide"
    );

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateCounter = () => {
      if (counter) counter.textContent = currentSlide + 1;
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateCounter();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateCounter();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    const variantRadios = container.querySelectorAll(
      '.variant-option input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".add-to-cart-btn");
    const variantInput = container.querySelector('input[name="id"]');
    const currentPriceEl = container.querySelector(".current-price");
    const comparePriceEl = container.querySelector(".compare-price");
    const savingsEl = container.querySelector(".savings .save-discount");
    const stockDataElement = container.querySelector(
      "#quick-add-variant-stock-data"
    );
    const stockData = stockDataElement
      ? JSON.parse(stockDataElement.textContent)
      : null;
    const variantStockEls = container.querySelectorAll(
      ".variant-option .option-value"
    );

    // Get variant count for button text formatting
    const shopifyVariantCount =
      this.currentProductData && Array.isArray(this.currentProductData)
        ? this.currentProductData.length
        : 0;

    if (variantRadios.length === 0) return;

    // Initialize stock indicators
    if (stockData) {
      this.updateStockIndicators(container, stockData, variantStockEls, []);
    }

    // Set initial button text based on current variant
    if (
      addToCartBtn &&
      this.currentProductData &&
      this.currentProductData.length > 0
    ) {
      const currentVariantId = variantInput
        ? parseInt(variantInput.value)
        : null;
      if (currentVariantId) {
        const currentVariant = this.currentProductData.find(
          (v) => v.id === currentVariantId
        );
        if (currentVariant) {
          if (currentVariant.available) {
            let variantText = currentVariant.options
              ? currentVariant.options.join("/")
              : "";
            const addToCartText =
              window.translations?.cart?.add_to_cart || "Add to Cart";

            if (shopifyVariantCount > 1 && variantText) {
              addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
            } else {
              addToCartBtn.textContent = addToCartText;
            }
            addToCartBtn.disabled = false;
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent =
              window.translations?.cart?.sold_out || "Sold Out";
          }
        }
      }
    }

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = this.getSelectedOptions(container);
        const selectedVariant = this.findVariantByOptions(selectedOptions);

        if (selectedVariant) {
          if (variantInput) variantInput.value = selectedVariant.id;

          // Update price display (matching main-product format)
          if (currentPriceEl) {
            currentPriceEl.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_without_trailing_zeros_format || window.money_format
            );
          }

          if (comparePriceEl && savingsEl) {
            if (selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceEl.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_without_trailing_zeros_format ||
                  window.money_format
              );
              comparePriceEl.style.display = "inline";
              const saveValue =
                100 -
                (selectedVariant.price / selectedVariant.compare_at_price) *
                  100;
              savingsEl.textContent = `${Math.round(saveValue)}% ${
                window.translations?.product?.saved || "Saved"
              }`;
              savingsEl.closest(".savings").style.display = "inline";
            } else {
              comparePriceEl.style.display = "none";
              savingsEl.closest(".savings").style.display = "none";
            }
          }

          // Update button (matching main-product format)
          if (addToCartBtn) {
            if (selectedVariant.available) {
              let variantText = selectedVariant.options
                ? selectedVariant.options.join("/")
                : "";
              const addToCartText =
                window.translations?.cart?.add_to_cart || "Add to Cart";

              if (shopifyVariantCount > 1 && variantText) {
                addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
              } else {
                addToCartBtn.textContent = addToCartText;
              }
              addToCartBtn.disabled = false;
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.textContent =
                window.translations?.cart?.sold_out || "Sold Out";
            }
          }

          // Update stock indicators
          if (stockData) {
            this.updateStockIndicators(
              container,
              stockData,
              variantStockEls,
              selectedOptions
            );
          }

          // Update image if variant has featured image
          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=800";
            const mainImage = container.querySelector(".quick-main-image");

            if (mainImage) {
              mainImage.src = newImageSrc;
              mainImage.alt = selectedVariant.featured_image.alt || "";
              mainImage.dataset.variantImage = selectedVariant.id;
            }
          }
        }
      });
    });
  }

  getSelectedOptions(container) {
    const selectedOptions = [];
    container.querySelectorAll(".variant-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const labels = group.querySelectorAll(".option-value");
      labels.forEach((label) => label.classList.remove("selected"));
      if (checked) {
        selectedOptions.push(checked.value);
        const checkedLabel = group.querySelector(`label[for="${checked.id}"]`);
        if (checkedLabel) checkedLabel.classList.add("selected");
      } else {
        selectedOptions.push(null);
      }
    });
    return selectedOptions;
  }

  findVariantByOptions(options) {
    if (!this.currentProductData) return null;
    return this.currentProductData.find((variant) => {
      return variant.options.every(
        (opt, idx) => !options[idx] || options[idx] === opt
      );
    });
  }

  updateStockIndicators(
    container,
    stockData,
    variantStockEls,
    selectedOptions = []
  ) {
    if (!stockData) return;
    const optionsCount = stockData.options.length;

    variantStockEls.forEach((el) => {
      const labelValue = el.getAttribute("title")?.trim();
      const fieldset = el.closest(".variant-option");
      const optionIndex = Array.from(fieldset.parentNode.children).indexOf(
        fieldset
      );

      // Remove old stock indicator
      el.querySelector(".variant-stock")?.remove();

      let stockLeft = 0;

      if (optionsCount === 1) {
        // Single option → sum directly
        stockLeft = stockData.variants
          .filter((v) => v.options[0] === labelValue)
          .reduce((acc, v) => acc + v.stock, 0);
      } else if (optionsCount >= 2) {
        if (optionIndex === 0) {
          // First-level (e.g., Size)
          stockLeft = stockData.variants
            .filter((v) => v.options[0] === labelValue)
            .reduce((acc, v) => acc + v.stock, 0);
        } else if (optionIndex === 1 && selectedOptions[0]) {
          // Second-level (e.g., Age) → based on selected Size
          stockLeft = stockData.variants
            .filter(
              (v) =>
                v.options[0] === selectedOptions[0] &&
                v.options[1] === labelValue
            )
            .reduce((acc, v) => acc + v.stock, 0);
        }
      }

      if (stockLeft > 0 && stockLeft < 10) {
        const urgent = stockLeft < 5;
        const isSwatch =
          el.hasAttribute("style") &&
          el.getAttribute("style").includes("--swatch");
        const div = document.createElement("div");
        div.className = `variant-stock ${
          isSwatch ? "variant-stock--swatch" : ""
        }`;
        div.dataset.stock = stockLeft;
        div.dataset.urgent = urgent ? "true" : "false";
        div.innerHTML = `
          <div class="dot ${urgent ? "urgent" : "warning"}"></div>
          ${stockLeft} left
        `;
        el.appendChild(div);
      }
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const variantInput = container.querySelector('input[name="id"]');
      if (!variantInput) return 9999;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      if (!variant) return 9999;

      const inventoryPolicy = variant.inventory_policy;
      const inventoryManagement = variant.inventory_management;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return variant.inventory_quantity || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    const getMinQuantity = () => {
      const minAttr = quantityInput.getAttribute("data-min");
      return minAttr ? parseInt(minAttr) : 1;
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const minValue = getMinQuantity();
      if (currentValue > minValue) {
        quantityInput.value = currentValue - 1;
        if (stockError) stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        return;
      }

      quantityInput.value = currentValue + 1;
      if (stockError) stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const maxQuantity = getMaxQuantity();
      const minValue = getMinQuantity();
      let newValue = parseInt(quantityInput.value) || minValue;

      if (newValue < minValue) newValue = minValue;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
      } else {
        if (stockError) stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector(".add-to-cart-btn");
      const originalText = submitBtn.textContent;
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const variantInput = form.querySelector('input[name="id"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      const maxQuantity = variant?.inventory_quantity || 9999;
      const inventoryPolicy = variant?.inventory_policy;
      const inventoryManagement = variant?.inventory_management;

      // Validate stock
      if (
        inventoryManagement === "shopify" &&
        inventoryPolicy === "deny" &&
        quantity > maxQuantity
      ) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        if (quantityInput) quantityInput.value = maxQuantity;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = window.translations?.cart?.adding || "Adding...";

      const lineItem = {
        id: variantId,
        quantity: Math.min(quantity, maxQuantity),
      };
      const csWrap = container.querySelector("[data-custom-size-field]");
      const csInput = container.querySelector("[data-custom-size-input]");
      const normCs = (x) => {
        if (x == null || x === "") return null;
        return String(x).trim();
      };
      if (csWrap && csWrap.classList.contains("is-visible") && csInput && variant) {
        const token = (
          csWrap.getAttribute("data-custom-size-trigger-token") || "custom"
        ).toLowerCase();
        const matches = ["option1", "option2", "option3"].some((k) => {
          const v = variant[k];
          return v != null && String(v).toLowerCase().indexOf(token) !== -1;
        });
        if (matches) {
          const raw = normCs(csInput.value);
          if (!raw) {
            if (stockError) stockError.textContent = "Please enter your custom size.";
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
            return;
          }
          const propName =
            csWrap.getAttribute("data-property-name") || "Custom size";
          lineItem.properties = { [propName]: raw };
        }
      }

      try {
        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [lineItem],
          }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = window.translations?.cart?.added || "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            product: this.currentProductData,
          });

          // Close modal after delay
          setTimeout(() => {
            this.close();
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error - Try Again";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }
  });
});
